// src/features/product/components/productsArchive/CategoriesBarLarge.tsx
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import type { CategoryWithChildren } from "../../helpers/productsCategoryHelpers";
import { hasCategoryProducts } from "../../helpers/productsCategoryHelpers";

interface CategoriesBarLargeProps {
  categories: CategoryWithChildren[];
  activeCategoryIds: number[];
  onCategoryClick: (categoryId: number | null) => void;
  isLoading?: boolean;
}

const CategoriesBarLarge: React.FC<CategoriesBarLargeProps> = ({
  categories,
  activeCategoryIds,
  onCategoryClick,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [hoveredParentId, setHoveredParentId] = useState<number | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Helper Functions for Timeout Management ---
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const startCloseTimeout = () => {
    clearCloseTimeout(); // Clear any existing timeout first
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredParentId(null); // Close the dropdown after the delay
    }, 200); // Adjust delay (milliseconds) as needed
  };

  // --- Event Handlers ---
  const handleMouseEnterItem = (categoryId: number) => {
    clearCloseTimeout(); // Stop any pending close action
    setHoveredParentId(categoryId); // Set the currently hovered parent
  };

  const handleMouseLeaveItem = () => {
    startCloseTimeout(); // Start the timer to close the dropdown when leaving the LI
  };

  // Handlers specifically for the dropdown div itself
  const handleMouseEnterDropdown = () => {
    clearCloseTimeout(); // Keep the dropdown open if the mouse enters it
  };

  const handleMouseLeaveDropdown = () => {
    startCloseTimeout(); // Start the timer to close when leaving the dropdown div
  };

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    categoryId: number | null
  ) => {
    e.preventDefault();
    onCategoryClick(categoryId);
    setHoveredParentId(null); // Close dropdown immediately on click
    clearCloseTimeout(); // Clear any pending close timeout on click
  };

  const isAllProductsActive = activeCategoryIds.length === 0;

  return (
    <nav
      className="categories-bar categories-bar--large"
      aria-label={t("filters.categories")}
      // Removed main mouseleave handler - handled by items now
    >
      <ul className="categories-bar__list categories-bar__list--parents">
        {/* All Products Button */}
        <li
          className={`categories-bar__item ${
            isAllProductsActive ? "is-active" : ""
          }`}
          // Add hover handlers if 'All Products' should interact with menu system
          onMouseEnter={() => handleMouseEnterItem(0)} // Use 0 or a non-category ID
          onMouseLeave={handleMouseLeaveItem}
        >
          {isLoading ? (
            <Skeleton width={100} height={36} />
          ) : (
            <a
              href="#"
              onClick={(e) => handleClick(e, null)}
              className="categories-bar__link"
              aria-current={isAllProductsActive ? "page" : undefined}
            >
              {t("filters.allProducts")}
            </a>
          )}
        </li>

        {/* Category Buttons */}
        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <li key={`skel-${index}`} className="categories-bar__item">
                <Skeleton width={120} height={36} />
              </li>
            ))
          : categories.map((category) => {
              const isActive = activeCategoryIds.includes(category.id);
              const subCategories =
                category.children?.filter(hasCategoryProducts) || [];
              const hasSubcategories = subCategories.length > 0;
              const isHovered = category.id === hoveredParentId;

              return (
                <li
                  key={category.id}
                  className={`categories-bar__item ${
                    isActive ? "is-active" : ""
                  }`}
                  onMouseEnter={() => handleMouseEnterItem(category.id)}
                  onMouseLeave={handleMouseLeaveItem} // Use the LI leave handler
                >
                  <a
                    href="#"
                    onClick={(e) => handleClick(e, category.id)}
                    className="categories-bar__link"
                    aria-current={isActive ? "page" : undefined}
                    aria-haspopup={hasSubcategories ? "true" : undefined}
                    aria-expanded={hasSubcategories && isHovered}
                  >
                    {category.name}
                    {hasSubcategories && (
                      <span className="categories-bar__arrow">▼</span>
                    )}
                  </a>

                  {/* Render Dropdown INSIDE the LI */}
                  {hasSubcategories && isHovered && (
                    <div
                      className="categories-bar__dropdown"
                      onMouseEnter={handleMouseEnterDropdown} // Keep open on dropdown enter
                      onMouseLeave={handleMouseLeaveDropdown} // Start close on dropdown leave
                    >
                      <ul className="categories-bar__list categories-bar__list--children">
                        {subCategories.map((subCategory) => {
                          const isSubActive = activeCategoryIds.includes(
                            subCategory.id
                          );
                          return (
                            <li
                              key={subCategory.id}
                              className={`categories-bar__item ${
                                isSubActive ? "is-active" : ""
                              }`}
                            >
                              <a
                                href="#"
                                onClick={(e) => handleClick(e, subCategory.id)}
                                className="categories-bar__link"
                                aria-current={isSubActive ? "page" : undefined}
                              >
                                {subCategory.name}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
      </ul>
    </nav>
  );
};

export default CategoriesBarLarge;
